import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Languages, UserCircle2 } from "lucide-react";
import { useT } from "@/hooks/useT";

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी · Hindi" },
  { code: "de", label: "Deutsch · German" },
  { code: "zh", label: "中文 · Mandarin" },
  { code: "es", label: "Español · Spanish" },
  { code: "fr", label: "Français · French" },
  { code: "ja", label: "日本語 · Japanese" },
  { code: "ko", label: "한국어 · Korean" },
  { code: "pt", label: "Português · Portuguese" },
  { code: "ru", label: "Русский · Russian" },
  { code: "it", label: "Italiano · Italian" },
  { code: "ar", label: "العربية · Arabic" },
  { code: "tr", label: "Türkçe · Turkish" },
  { code: "id", label: "Bahasa · Indonesian" },
  { code: "nl", label: "Nederlands · Dutch" },
  { code: "vi", label: "Tiếng Việt · Vietnamese" },
  { code: "ta", label: "தமிழ் · Tamil" },
  { code: "te", label: "తెలుగు · Telugu" },
  { code: "bn", label: "বাংলা · Bengali" },
  { code: "mr", label: "मराठी · Marathi" },
  { code: "gu", label: "ગુજરાતી · Gujarati" },
  { code: "pa", label: "ਪੰਜਾਬੀ · Punjabi" },
  { code: "ml", label: "മലയാളം · Malayalam" },
  { code: "kn", label: "ಕನ್ನಡ · Kannada" },
  { code: "ur", label: "اردو · Urdu" },
];

export const PERSONAS = [
  { code: "expert", label: "Expert", desc: "Clinical, precise, densely technical" },
  { code: "poetic", label: "Poetic", desc: "Lyrical, metaphor-rich, literary" },
  { code: "motivational", label: "Motivational", desc: "Coach-like, energetic, action-oriented" },
];

export default function LanguagePersonaBar({ compact = false, onChange }) {
  const [prefs, setPrefs] = useState({ language: "en", persona: "expert" });
  const { setLang } = useT();

  useEffect(() => {
    api.get("/user/prefs").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);

  const update = async (partial) => {
    const next = { ...prefs, ...partial };
    setPrefs(next);
    try {
      await api.put("/user/prefs", next);
      if (partial.language) setLang(partial.language);
      onChange?.(next);
    } catch {}
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${compact ? "" : "p-4 glass !rounded-2xl"}`}
      data-testid="lang-persona-bar"
    >
      <div className="flex items-center gap-2">
        <Languages size={14} className="text-[#00FF9D]" />
        <select
          value={prefs.language}
          onChange={(e) => update({ language: e.target.value })}
          className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white/85 focus:outline-none focus:border-[#0044FF]/60"
          data-testid="lang-select"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code} className="bg-[#0B0E14]">
              {l.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <UserCircle2 size={14} className="text-[#ffb067]" />
        <div className="flex rounded-lg border border-white/10 bg-black/40 overflow-hidden">
          {PERSONAS.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => update({ persona: p.code })}
              className={`text-xs px-3 py-1.5 transition ${
                prefs.persona === p.code
                  ? "bg-[#0044FF]/30 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
              title={p.desc}
              data-testid={`persona-${p.code}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
